import type { CreateOrderDraft, LiveOrder } from './my-cases.types';

/**
 * Fields the operator fills in. Compared one by one rather than via JSON so a
 * key added to CreateOrderDraft without being listed here fails the spec instead
 * of silently never counting as an edit.
 */
const DRAFT_FIELDS: (keyof CreateOrderDraft)[] = [
  'service_model',
  'category',
  'service_name',
  'scheduledDate',
  'scheduledTime',
  'providerDisplayText',
  'provider_assignment_type',
  'provider_id',
  'address',
  'address_access_notes',
  'internal_notes',
  'payment_type',
  'quote_amount',
  'estimated_duration_minutes',
  'completion_notes',
  'cancelled_reason',
];

/**
 * Has the operator actually changed anything since the drawer opened?
 *
 * The baseline is whatever the drawer was seeded with — the default draft for a
 * new order, the order's own values for an edit — so this answers "is there work
 * to lose", which is the only question the discard and case-switch prompts care
 * about.
 */
export function isDraftDirty(draft: CreateOrderDraft, baseline: CreateOrderDraft): boolean {
  return DRAFT_FIELDS.some((field) => !valuesEqual(draft[field], baseline[field]));
}

/**
 * Empty is empty: the form writes '' where the draft builders write null (and
 * vice versa) depending on which control touched the field. Treating those as
 * different would mark an untouched draft dirty and prompt the operator over
 * nothing.
 */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a instanceof Date || b instanceof Date) {
    const aTime = a instanceof Date ? a.getTime() : null;
    const bTime = b instanceof Date ? b.getTime() : null;
    return aTime === bTime;
  }

  if (isEmpty(a) && isEmpty(b)) return true;
  return a === b;
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** Label for the collapsed draft, so the operator knows what is waiting for them. */
export function describeDraft(editingOrder: LiveOrder | null): string {
  return editingOrder ? `Editing ${editingOrder.order_code}` : 'Order draft · Create';
}
